import { ProfileRelationshipEnum } from "../../../graphql/supergraphEnums";
import { ComplianceCaseTypeEnum } from "../../../graphql/supergraphTypes";
import { Programs } from "../../../utils/compliance_program";
import { allDocumentsUploaded } from "../../documents/DocumentCollectorInvestor/utils";
import { isDocumentExpired } from "../../documents/utils";
import { PROFILE_STATUS_RULES_MATRIX } from "./profile_status_rules";
import {
  ControllersKey,
  OwnersKey,
  CompletionDocumentStatus,
  ProfileCategory,
  ProfileTypeForStatus,
  ProfileDisplayStatus,
  ProfileCompletionStatus,
  ParentRelationshipForStatus,
  DataEntryStatusForStatus,
  DocumentForStatus,
  RequestedDocumentTypeForStatus,
  ShowDetailsKey,
} from "./types";

const showOwner = (
  parentRelationships: ParentRelationshipForStatus[],
  ownersKey: OwnersKey,
): boolean => {
  switch (ownersKey) {
    case OwnersKey.YES:
    case OwnersKey.JOINT_MIN_TWO_CHILDREN:
      return true;
    case OwnersKey.RELATIONSHIP_CRITERIA:
      return !!parentRelationships.some(
        (rel) => rel.relationshipType === ProfileRelationshipEnum.Owner,
      );
    default:
      return false;
  }
};

const hasMinimumTwoChildren = (directChildCount?: number): boolean =>
  (directChildCount ?? 0) >= 2;

const showController = (
  parentRelationships: ParentRelationshipForStatus[],
  controllersKey: ControllersKey,
) => {
  switch (controllersKey) {
    case ControllersKey.YES:
      return true;
    case ControllersKey.RELATIONSHIP_CRITERIA:
      return !!parentRelationships.some(
        (rel) => rel.relationshipType === ProfileRelationshipEnum.Controller,
      );
    case ControllersKey.RELATIONSHIP_CRITERIA_AML:
      return !!parentRelationships.some(
        (rel) =>
          rel.relationshipType === ProfileRelationshipEnum.Owner ||
          rel.relationshipType === ProfileRelationshipEnum.Controller,
      );
    default:
      return false;
  }
};

const hasAnyExpiredDocument = (
  documents?: DocumentForStatus[] | null,
  documentTypes?: RequestedDocumentTypeForStatus[],
): boolean => {
  if (!documentTypes?.length) return false;
  return (documents || []).some(
    (doc) =>
      isDocumentExpired(doc.expiryDate) &&
      doc.documentPurpose != null &&
      documentTypes.some(
        (dt) =>
          dt.baseType === doc.documentPurpose!.baseType &&
          (!dt.subType || doc.metadata?.find((m) => m.key === "type")?.value === dt.subType),
      ),
  );
};

const computeDocumentStatus = (
  dataEntryStatus?: DataEntryStatusForStatus | null,
  documentTypes?: RequestedDocumentTypeForStatus[],
  documents?: DocumentForStatus[] | null,
): CompletionDocumentStatus | null => {
  const hasExpired = hasAnyExpiredDocument(documents, documentTypes);

  if (
    allDocumentsUploaded(documentTypes || []) &&
    dataEntryStatus?.documentsCompleted &&
    !hasExpired
  ) {
    return CompletionDocumentStatus.COMPLETED;
  }
  if (hasExpired) {
    return CompletionDocumentStatus.EXPIRED;
  }
  if (dataEntryStatus?.documentsCompleted) {
    return CompletionDocumentStatus.ACKNOWLEDGED;
  }
  return CompletionDocumentStatus.NOT_STARTED;
};

export function getProfileDisplayStatus(
  casetType: ComplianceCaseTypeEnum,
  programType: Programs,
  profileCategory: ProfileCategory,
  isRoot: boolean,
  surveyAvailable: boolean,
  profileData: ProfileTypeForStatus | null,
): ProfileDisplayStatus {
  const matchingRule = PROFILE_STATUS_RULES_MATRIX.find(
    (rule) =>
      rule.context === casetType &&
      rule.profileCategory === profileCategory &&
      rule.isRoot === isRoot &&
      rule.programType.includes(programType),
  );

  if (matchingRule) {
    const { detailsKey, documentsStatus, ownersKey, controllersKey } = matchingRule;

    // Safely handle parentRelationships from either schema
    const parentRelationships = profileData?.parentRelationships || [];
    const ownerAvailability = showOwner(parentRelationships, ownersKey);
    const controllerAvailability = showController(parentRelationships, controllersKey);

    const amlEntityIdMissing =
      detailsKey === ShowDetailsKey.AML_ENTITY_ID && !profileData?.amlEntityTypeId;
    // ShowDetailsKey.AML_ENTITY_ID,
    // If the amlEntityTypeId is not avaialble, we need to show the details even after collecting the details.
    const storageKey = `jsq_details_aml_${profileData?.id}`;
    if (amlEntityIdMissing) {
      localStorage.setItem(storageKey, "true");
    }
    const persisted =
      detailsKey === ShowDetailsKey.AML_ENTITY_ID &&
      localStorage.getItem(storageKey) === "true";

    return {
      detailsAvailability:
        detailsKey === ShowDetailsKey.YES ||
        (detailsKey === ShowDetailsKey.SURVEY_COUNT && surveyAvailable) ||
        persisted,
      documentsAvailability: documentsStatus,
      ownerAvailability: ownerAvailability,
      controllerAvailability: controllerAvailability,
    };
  }

  return {
    detailsAvailability: false,
    documentsAvailability: false,
    ownerAvailability: false,
    controllerAvailability: false,
  };
}

export function getProfileCompletionStatus(
  casetType: ComplianceCaseTypeEnum,
  programType: Programs,
  profileCategory: ProfileCategory,
  isRoot: boolean,
  requestedDocumentTypes: RequestedDocumentTypeForStatus[],
  profileData: ProfileTypeForStatus | null,
): ProfileCompletionStatus {
  const matchingRule = PROFILE_STATUS_RULES_MATRIX.find(
    (rule) =>
      rule.context === casetType &&
      rule.profileCategory === profileCategory &&
      rule.isRoot === isRoot &&
      rule.programType.includes(programType),
  );

  if (matchingRule) {
    const { detailsCompletionKey, documentsStatus, ownersKey, controllersKey } = matchingRule;

    const parentRelationships = profileData?.parentRelationships || [];
    const ownerAvailability = showOwner(parentRelationships, ownersKey);
    const controllerAvailability = showController(parentRelationships, controllersKey);

    let ownersCompletionStatus: boolean | null = null;
    if (ownerAvailability) {
      switch (ownersKey) {
        case OwnersKey.JOINT_MIN_TWO_CHILDREN:
          ownersCompletionStatus = !!(
            profileData?.caseDataEntryStatus?.ownersCompleted &&
            hasMinimumTwoChildren(profileData?.childOwnersCount)
          );
          break;
        case OwnersKey.YES:
        case OwnersKey.RELATIONSHIP_CRITERIA:
          ownersCompletionStatus = !!profileData?.caseDataEntryStatus?.ownersCompleted;
          break;
      }
    }

    return {
      detailsCompletion: detailsCompletionKey
        ? detailsCompletionKey.every((key) => !!profileData?.[key])
        : null,
      documentsCompletionStatus: documentsStatus
        ? computeDocumentStatus(
            profileData?.caseDataEntryStatus,
            requestedDocumentTypes,
            profileData?.documents,
          )
        : null,
      ownersCompletionStatus,
      controllersCompletionStatus: controllerAvailability
        ? !!profileData?.caseDataEntryStatus?.controllersCompleted
        : null,
    };
  }

  return {
    detailsCompletion: false,
    documentsCompletionStatus: null,
    ownersCompletionStatus: false,
    controllersCompletionStatus: false,
  };
}
